// ============================================================================
// ROOM HOOKS
// ============================================================================
// React hooks for Room API operations

import { useState, useEffect, useCallback } from 'react';
import { roomService } from 'api/services/roomService';
import type {
  Room,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all rooms (paginated)
 */
export const useRooms = (params?: RoomSearchRequest) => {
  const [data, setData] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roomService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchRooms,
  };
};

/**
 * Hook to fetch a single room by ID
 */
export const useRoom = (id: ID) => {
  const [data, setData] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRoom = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await roomService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRoom();
  }, [fetchRoom]);

  return {
    data,
    loading,
    error,
    refetch: fetchRoom,
  };
};

/**
 * Hook to create a new room
 */
export const useCreateRoom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRoom = async (
    data: CreateRoomRequest,
    options?: {
      onSuccess?: (room: Room) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await roomService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createRoom,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a room
 */
export const useUpdateRoom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRoom = async (
    id: ID,
    data: UpdateRoomRequest,
    options?: {
      onSuccess?: (room: Room) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await roomService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateRoom,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a room
 */
export const useDeleteRoom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRoom = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await roomService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteRoom,
    loading,
    error,
    success,
  };
};
